<template>
  <div class="space-y-6">
    <!-- State Selector -->
    <div>
      <label class="form-label">1. Select States</label>
      <SearchableMultiSelect
        v-model="localFilters.stateIds"
        :options="statesWithLabels"
        placeholder="Search states..."
        label-key="label"
        value-key="id"
        @change="handleStateChange" />
    </div>

    <!-- County Selector (conditional) -->
    <div v-if="localFilters.stateIds.length > 0 && counties.length > 0">
      <label class="form-label">2. Select Counties (Optional)</label>
      <SearchableMultiSelect
        v-model="localFilters.countyFips"
        :options="countiesWithLabels"
        placeholder="Search counties..."
        label-key="label"
        value-key="fips"
        @change="handleFilterChange" />
      <p class="mt-1 text-xs text-gray-500">
        Leave empty to include all counties in selected states
      </p>
    </div>

    <!-- Population Filter -->
    <div v-if="localFilters.stateIds.length > 0">
      <PopulationFilter
        v-model:minPopulation="localFilters.minPopulation"
        v-model:maxPopulation="localFilters.maxPopulation"
        :matching-count="matchingCitiesCount"
        :loading="loadingPreview"
        @apply="handleFilterChange" />
    </div>

    <!-- City Selection -->
    <div v-if="localFilters.stateIds.length > 0 && matchingCitiesCount > 0">
      <div class="flex items-center justify-between mb-2">
        <label class="form-label mb-0">3. Select Cities</label>
        <div class="flex gap-2">
          <button
            type="button"
            @click="selectAllCities"
            class="text-xs text-blue-600 hover:text-blue-800">
            Select All
          </button>
          <span class="text-gray-300">|</span>
          <button
            type="button"
            @click="deselectAllCities"
            class="text-xs text-blue-600 hover:text-blue-800">
            Clear
          </button>
        </div>
      </div>

      <!-- City Search -->
      <div class="relative mb-2">
        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg class="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <input
          v-model="citySearch"
          type="text"
          placeholder="Search cities..."
          class="form-input form-input-sm w-full"
          style="padding-left: 2.5rem;">
      </div>

      <!-- City List -->
      <div class="border border-gray-200 rounded-md max-h-64 overflow-y-auto bg-white">
        <div v-if="loadingCities" class="p-4 text-center text-sm text-gray-500">
          <svg class="animate-spin h-5 w-5 mx-auto mb-2 text-blue-500" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Loading cities...
        </div>
        <div v-else-if="filteredCityList.length === 0" class="p-4 text-center text-sm text-gray-500">
          No cities found
        </div>
        <div v-else>
          <label
            v-for="city in filteredCityList"
            :key="city.id"
            class="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0">
            <input
              type="checkbox"
              :value="city.id"
              v-model="selectedCityIds"
              class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded">
            <span class="ml-3 text-sm text-gray-700 flex-1">
              {{ city.city }}, {{ city.state_id }}
            </span>
            <span class="text-xs text-gray-400">
              {{ formatNumber(city.population) }}
            </span>
          </label>
        </div>
      </div>
      <p class="mt-1 text-xs text-gray-500">
        {{ selectedCityIds.length > 0 ? `${selectedCityIds.length} cities selected` : 'Select specific cities or add all matching' }}
      </p>
    </div>

    <!-- Selection Summary -->
    <div v-if="localFilters.stateIds.length > 0" class="bg-blue-50 border border-blue-200 rounded-md p-4">
      <h4 class="text-sm font-medium text-blue-900 mb-2">
        Selection Summary
      </h4>
      <ul class="text-sm text-blue-700 space-y-1">
        <li>
          <span class="font-medium">States:</span> {{ localFilters.stateIds.join(', ') }}
        </li>
        <li v-if="localFilters.countyFips.length > 0">
          <span class="font-medium">Counties:</span> {{ localFilters.countyFips.length }} selected
        </li>
        <li v-if="localFilters.minPopulation">
          <span class="font-medium">Population:</span> {{ formatNumber(localFilters.minPopulation) }}+
          <span v-if="localFilters.maxPopulation">
            to {{ formatNumber(localFilters.maxPopulation) }}
          </span>
        </li>
        <li class="font-semibold pt-2 border-t border-blue-300 mt-2">
          <span class="font-medium">Cities to Add:</span>
          {{ selectedCityIds.length > 0 ? selectedCityIds.length : formatNumber(matchingCitiesCount) }}
          <span v-if="selectedCityIds.length === 0" class="font-normal text-blue-600">(all matching)</span>
        </li>
      </ul>
    </div>

    <!-- Action Buttons -->
    <div v-if="localFilters.stateIds.length > 0" class="flex gap-2">
      <button
        type="button"
        @click="handleAddLocations"
        :disabled="citiesToAddCount === 0 || adding"
        class="btn btn-primary"
        :class="{ 'opacity-50 cursor-not-allowed': citiesToAddCount === 0 || adding }">
        <svg v-if="adding" class="w-4 h-4 mr-1 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        <svg v-else class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        {{ adding ? 'Adding...' : `Add ${formatNumber(citiesToAddCount)} Location${citiesToAddCount === 1 ? '' : 's'}` }}
      </button>
      <button
        type="button"
        @click="$emit('cancel')"
        class="btn btn-ghost">
        Cancel
      </button>
    </div>

    <!-- Error Message -->
    <div v-if="error" class="bg-red-50 border border-red-200 rounded-md p-3">
      <p class="text-sm text-red-600">
        {{ error }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import SearchableMultiSelect from './SearchableMultiSelect.vue'
import PopulationFilter from './PopulationFilter.vue'

const props = defineProps({
  websiteId: {
    type: String,
    required: true
  },
  citiesApiUrl: {
    type: String,
    default: '/ajax/cities'
  },
  createUrl: {
    type: String,
    required: true
  },
  csrfToken: {
    type: String,
    required: true
  }
})

const emit = defineEmits(['cancel', 'success'])

// State
const states = ref([])
const counties = ref([])
const cities = ref([])
const selectedCityIds = ref([])
const citySearch = ref('')
const loadingStates = ref(false)
const loadingCounties = ref(false)
const loadingCities = ref(false)
const loadingPreview = ref(false)
const adding = ref(false)
const error = ref(null)
const matchingCitiesCount = ref(0)

const localFilters = ref({
  stateIds: [],
  countyFips: [],
  minPopulation: 10000,
  maxPopulation: null
})

// Computed
const statesWithLabels = computed(() => states.value.map((state) => ({
  id: state.id,
  label: `${state.name} (${state.id})`
})))

const countiesWithLabels = computed(() => counties.value.map((county) => ({
  fips: county.fips,
  label: `${county.name}, ${county.state_id}`
})))

const filteredCityList = computed(() => {
  if (!citySearch.value) return cities.value
  const search = citySearch.value.toLowerCase()
  return cities.value.filter((city) => city.city.toLowerCase().includes(search) ||
    city.state_id.toLowerCase().includes(search))
})

const citiesToAddCount = computed(() => (selectedCityIds.value.length > 0 ? selectedCityIds.value.length : matchingCitiesCount.value))

// Methods
const formatNumber = (num) => num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')

const loadStates = async() => {
  loadingStates.value = true
  try {
    const response = await fetch(`${props.citiesApiUrl}/states`)
    const data = await response.json()
    states.value = data.states || []
  } catch (err) {
    console.error('[LocationSelector] Failed to load states:', err)
    error.value = `Failed to load states: ${err.message}`
  } finally {
    loadingStates.value = false
  }
}

const loadCounties = async() => {
  if (localFilters.value.stateIds.length === 0) {
    counties.value = []
    return
  }

  loadingCounties.value = true
  try {
    const params = new URLSearchParams()
    localFilters.value.stateIds.forEach((id) => params.append('state_ids[]', id))

    const response = await fetch(`${props.citiesApiUrl}/counties?${params}`)
    const data = await response.json()
    counties.value = data.counties || []
  } catch (err) {
    console.error('[LocationSelector] Failed to load counties:', err)
  } finally {
    loadingCounties.value = false
  }
}

const loadCities = async() => {
  if (localFilters.value.stateIds.length === 0) {
    cities.value = []
    return
  }

  loadingCities.value = true
  try {
    const params = new URLSearchParams()
    localFilters.value.stateIds.forEach((id) => params.append('state_ids[]', id))

    if (localFilters.value.countyFips.length > 0) {
      localFilters.value.countyFips.forEach((fips) => params.append('county_fips[]', fips))
    }

    if (localFilters.value.minPopulation) {
      params.append('min_population', localFilters.value.minPopulation)
    }
    if (localFilters.value.maxPopulation) {
      params.append('max_population', localFilters.value.maxPopulation)
    }
    params.append('limit', '500')

    const response = await fetch(`${props.citiesApiUrl}?${params}`)
    const data = await response.json()
    cities.value = data.cities || []
  } catch (err) {
    console.error('[LocationSelector] Failed to load cities:', err)
  } finally {
    loadingCities.value = false
  }
}

const loadPreviewCount = async() => {
  if (localFilters.value.stateIds.length === 0) {
    matchingCitiesCount.value = 0
    return
  }

  loadingPreview.value = true
  try {
    const params = new URLSearchParams()
    localFilters.value.stateIds.forEach((id) => params.append('state_ids[]', id))

    if (localFilters.value.countyFips.length > 0) {
      localFilters.value.countyFips.forEach((fips) => params.append('county_fips[]', fips))
    }

    if (localFilters.value.minPopulation) {
      params.append('min_population', localFilters.value.minPopulation)
    }
    if (localFilters.value.maxPopulation) {
      params.append('max_population', localFilters.value.maxPopulation)
    }

    const response = await fetch(`${props.citiesApiUrl}/filter_preview?${params}`)
    const data = await response.json()
    matchingCitiesCount.value = data.total_count || 0
  } catch (err) {
    console.error('[LocationSelector] Failed to load preview:', err)
  } finally {
    loadingPreview.value = false
  }
}

const handleStateChange = () => {
  localFilters.value.countyFips = []
  selectedCityIds.value = []
  loadCounties()
  loadCities()
  loadPreviewCount()
}

const handleFilterChange = () => {
  selectedCityIds.value = []
  loadCities()
  loadPreviewCount()
}

const selectAllCities = () => {
  selectedCityIds.value = cities.value.map((c) => c.id)
}

const deselectAllCities = () => {
  selectedCityIds.value = []
}

const handleAddLocations = async() => {
  const count = citiesToAddCount.value
  if (count === 0) return

  const useSelected = selectedCityIds.value.length > 0
  const message = useSelected ?
    `Add ${count} selected cities as service areas?` :
    `Add all ${formatNumber(count)} matching cities as service areas?`

  if (!confirm(message)) {
    return
  }

  adding.value = true
  error.value = null

  try {
    let cityIds

    if (useSelected) {
      // Use the selected city IDs directly
      cityIds = selectedCityIds.value
    } else {
      // Fetch all city IDs matching the filter
      const params = new URLSearchParams()
      localFilters.value.stateIds.forEach((id) => params.append('state_ids[]', id))

      if (localFilters.value.countyFips.length > 0) {
        localFilters.value.countyFips.forEach((fips) => params.append('county_fips[]', fips))
      }

      if (localFilters.value.minPopulation) {
        params.append('min_population', localFilters.value.minPopulation)
      }
      if (localFilters.value.maxPopulation) {
        params.append('max_population', localFilters.value.maxPopulation)
      }
      params.append('limit', '5000')

      const citiesResponse = await fetch(`${props.citiesApiUrl}?${params}`)
      const citiesData = await citiesResponse.json()

      if (!citiesData.cities || citiesData.cities.length === 0) {
        error.value = 'No cities found matching your criteria'
        return
      }

      cityIds = citiesData.cities.map((c) => c.id)
    }

    // Now create the service areas
    const response = await fetch(props.createUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': props.csrfToken,
        'Accept': 'application/json'
      },
      body: JSON.stringify({ city_ids: cityIds })
    })

    const data = await response.json()

    if (response.ok) {
      emit('success', data)
      // Reload page to show new service areas
      window.location.href = `${window.location.pathname}#locations`
      window.location.reload()
    } else {
      error.value = data.error || 'Failed to add locations'
    }
  } catch (err) {
    console.error('[LocationSelector] Failed to add locations:', err)
    error.value = `Error: ${err.message}`
  } finally {
    adding.value = false
  }
}

// Watch for filter changes to update preview
watch(() => localFilters.value.minPopulation, handleFilterChange)
watch(() => localFilters.value.maxPopulation, handleFilterChange)

// Lifecycle
onMounted(() => {
  loadStates()
})
</script>
